import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AccountOfficerDashboardStats,
  AccountOfficerAssignedCustomerWithRisk,
  AccountOfficerAssignedCustomersResponse,
  AccountOfficerOrdersOverviewItem,
  AccountOfficerOverview,
  AccountOfficerRevenueTrend,
  AccountOfficerTopCustomer,
  PeriodFilter,
  AccountOfficerCustomerActivity,
  AccountOfficerCustomerOrderHistory,
  AccountOfficerCustomerOrderHistoryResponse,
} from "../../types/types";
import {
  getAccountOfficerAssignedCustomersWithRisk,
  getAccountOfficerDashboardStats,
  getAccountOfficerOrdersOverview,
  getAccountOfficerOverview,
  getAccountOfficerRevenueTrend,
  getAccountOfficerTopCustomersByRevenue,
  getAccountOfficerCustomerActivity,
  getAccountOfficerCustomerOrderHistory,
} from "../thunk";

type LoadStatus = "idle" | "loading" | "succeeded" | "failed";

interface AccountOfficerState {
  dashboardStats: AccountOfficerDashboardStats | null;
  dashboardStatsStatus: LoadStatus;
  dashboardStatsError: string | null;

  overview: AccountOfficerOverview | null;
  overviewStatus: LoadStatus;
  overviewError: string | null;

  revenueTrend: AccountOfficerRevenueTrend | null;
  revenueTrendStatus: LoadStatus;
  revenueTrendError: string | null;
  revenueTrendPeriod: PeriodFilter;

  ordersOverview: AccountOfficerOrdersOverviewItem[];
  ordersOverviewStatus: LoadStatus;
  ordersOverviewError: string | null;
  ordersOverviewPeriod: PeriodFilter;

  topCustomers: AccountOfficerTopCustomer[];
  topCustomersStatus: LoadStatus;
  topCustomersError: string | null;

  assignedCustomers: AccountOfficerAssignedCustomerWithRisk[];
  assignedCustomersStatus: LoadStatus;
  assignedCustomersError: string | null;
  assignedCustomersPage: number;
  assignedCustomersLimit: number;
  assignedCustomersTotal: number;
  assignedCustomersTotalPages: number;

  activities: AccountOfficerCustomerActivity[];
  activitiesStatus: LoadStatus;
  activitiesError: string | null;

  selectedCustomerHistory: AccountOfficerCustomerOrderHistory | null;
  selectedCustomerHistoryStatus: LoadStatus;
  selectedCustomerHistoryError: string | null;
}

const initialState: AccountOfficerState = {
  dashboardStats: null,
  dashboardStatsStatus: "idle",
  dashboardStatsError: null,

  overview: null,
  overviewStatus: "idle",
  overviewError: null,

  revenueTrend: null,
  revenueTrendStatus: "idle",
  revenueTrendError: null,
  revenueTrendPeriod: "monthly",

  ordersOverview: [],
  ordersOverviewStatus: "idle",
  ordersOverviewError: null,
  ordersOverviewPeriod: "monthly",

  topCustomers: [],
  topCustomersStatus: "idle",
  topCustomersError: null,

  assignedCustomers: [],
  assignedCustomersStatus: "idle",
  assignedCustomersError: null,
  assignedCustomersPage: 1,
  assignedCustomersLimit: 10,
  assignedCustomersTotal: 0,
  assignedCustomersTotalPages: 1,

  activities: [],
  activitiesStatus: "idle",
  activitiesError: null,

  selectedCustomerHistory: null,
  selectedCustomerHistoryStatus: "idle",
  selectedCustomerHistoryError: null,
};

const accountOfficerSlice = createSlice({
  name: "accountOfficer",
  initialState,
  reducers: {
    setRevenueTrendPeriod: (state, action: PayloadAction<PeriodFilter>) => {
      state.revenueTrendPeriod = action.payload;
    },
    setOrdersOverviewPeriod: (state, action: PayloadAction<PeriodFilter>) => {
      state.ordersOverviewPeriod = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccountOfficerDashboardStats.pending, (state) => {
        state.dashboardStatsStatus = "loading";
        state.dashboardStatsError = null;
      })
      .addCase(
        getAccountOfficerDashboardStats.fulfilled,
        (state, action: PayloadAction<AccountOfficerDashboardStats>) => {
          state.dashboardStatsStatus = "succeeded";
          state.dashboardStats = action.payload;
        }
      )
      .addCase(getAccountOfficerDashboardStats.rejected, (state, action) => {
        state.dashboardStatsStatus = "failed";
        state.dashboardStatsError =
          (action.payload as string) || "Failed to fetch dashboard stats";
      })

      .addCase(getAccountOfficerOverview.pending, (state) => {
        state.overviewStatus = "loading";
        state.overviewError = null;
      })
      .addCase(
        getAccountOfficerOverview.fulfilled,
        (state, action: PayloadAction<AccountOfficerOverview>) => {
          state.overviewStatus = "succeeded";
          state.overview = action.payload;
        }
      )
      .addCase(getAccountOfficerOverview.rejected, (state, action) => {
        state.overviewStatus = "failed";
        state.overviewError =
          (action.payload as string) || "Failed to fetch overview";
      })

      .addCase(getAccountOfficerRevenueTrend.pending, (state, action) => {
        state.revenueTrendStatus = "loading";
        state.revenueTrendError = null;
        state.revenueTrendPeriod = action.meta.arg.period;
      })
      .addCase(
        getAccountOfficerRevenueTrend.fulfilled,
        (state, action: PayloadAction<AccountOfficerRevenueTrend>) => {
          state.revenueTrendStatus = "succeeded";
          state.revenueTrend = action.payload;
        }
      )
      .addCase(getAccountOfficerRevenueTrend.rejected, (state, action) => {
        state.revenueTrendStatus = "failed";
        state.revenueTrendError =
          (action.payload as string) || "Failed to fetch revenue trend";
      })

      .addCase(getAccountOfficerOrdersOverview.pending, (state, action) => {
        state.ordersOverviewStatus = "loading";
        state.ordersOverviewError = null;
        state.ordersOverviewPeriod = action.meta.arg.period;
      })
      .addCase(
        getAccountOfficerOrdersOverview.fulfilled,
        (
          state,
          action: PayloadAction<{
            period: PeriodFilter;
            data: AccountOfficerOrdersOverviewItem[];
          }>
        ) => {
          state.ordersOverviewStatus = "succeeded";
          state.ordersOverview = action.payload.data;
          state.ordersOverviewPeriod = action.payload.period;
        }
      )
      .addCase(getAccountOfficerOrdersOverview.rejected, (state, action) => {
        state.ordersOverviewStatus = "failed";
        state.ordersOverviewError =
          (action.payload as string) || "Failed to fetch orders overview";
      })

      .addCase(getAccountOfficerTopCustomersByRevenue.pending, (state) => {
        state.topCustomersStatus = "loading";
        state.topCustomersError = null;
      })
      .addCase(
        getAccountOfficerTopCustomersByRevenue.fulfilled,
        (state, action: PayloadAction<AccountOfficerTopCustomer[]>) => {
          state.topCustomersStatus = "succeeded";
          state.topCustomers = action.payload;
        }
      )
      .addCase(
        getAccountOfficerTopCustomersByRevenue.rejected,
        (state, action) => {
          state.topCustomersStatus = "failed";
          state.topCustomersError =
            (action.payload as string) || "Failed to fetch top customers";
        }
      )

      .addCase(getAccountOfficerAssignedCustomersWithRisk.pending, (state, action) => {
        state.assignedCustomersStatus = "loading";
        state.assignedCustomersError = null;
        state.assignedCustomersPage = action.meta.arg.page ?? 1;
        state.assignedCustomersLimit = action.meta.arg.limit ?? 10;
      })
      .addCase(
        getAccountOfficerAssignedCustomersWithRisk.fulfilled,
        (state, action: PayloadAction<AccountOfficerAssignedCustomersResponse>) => {
          state.assignedCustomersStatus = "succeeded";
          state.assignedCustomers = action.payload.data;
          state.assignedCustomersPage = action.payload.page;
          state.assignedCustomersLimit = action.payload.limit;
          state.assignedCustomersTotal = action.payload.total;
          state.assignedCustomersTotalPages = action.payload.totalPages;
        }
      )
      .addCase(getAccountOfficerAssignedCustomersWithRisk.rejected, (state, action) => {
        state.assignedCustomersStatus = "failed";
        state.assignedCustomersError =
          (action.payload as string) || "Failed to fetch assigned customers";
      })

      .addCase(getAccountOfficerCustomerActivity.pending, (state) => {
        state.activitiesStatus = "loading";
        state.activitiesError = null;
      })
      .addCase(
        getAccountOfficerCustomerActivity.fulfilled,
        (state, action: PayloadAction<AccountOfficerCustomerActivity[]>) => {
          state.activitiesStatus = "succeeded";
          state.activities = action.payload;
        }
      )
      .addCase(getAccountOfficerCustomerActivity.rejected, (state, action) => {
        state.activitiesStatus = "failed";
        state.activitiesError =
          (action.payload as string) || "Failed to fetch activities";
      })

      .addCase(getAccountOfficerCustomerOrderHistory.pending, (state) => {
        state.selectedCustomerHistoryStatus = "loading";
        state.selectedCustomerHistoryError = null;
      })
      .addCase(
        getAccountOfficerCustomerOrderHistory.fulfilled,
        (state, action: PayloadAction<AccountOfficerCustomerOrderHistoryResponse>) => {
          state.selectedCustomerHistoryStatus = "succeeded";
          // We assume the API returns the history for the requested customer in the first element of the array
          state.selectedCustomerHistory = action.payload.customers[0] || null;
        }
      )
      .addCase(getAccountOfficerCustomerOrderHistory.rejected, (state, action) => {
        state.selectedCustomerHistoryStatus = "failed";
        state.selectedCustomerHistoryError =
          (action.payload as string) || "Failed to fetch customer history";
      });
  },
});

export const { setRevenueTrendPeriod, setOrdersOverviewPeriod } =
  accountOfficerSlice.actions;
export default accountOfficerSlice.reducer;
